import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { getNftMediaUrl } from '@/core/services/storage';
import type { Claim } from '@/core/types';

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 8px 2px rgba(255, 165, 0, 0.6);
  }
  50% {
    box-shadow: 0 0 16px 4px rgba(255, 165, 0, 0.9);
  }
`;

interface CertCardProps {
  claim: Claim;
  highlighted?: boolean;
  onClick?: () => void;
}

export const CertCard = forwardRef<HTMLDivElement, CertCardProps>(function CertCard(
  { claim, highlighted = false, onClick },
  ref
) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  // ホバー時に再生、離れたら停止
  const handleMouseEnter = () => {
    videoRef.current?.play().catch(() => {
      // AbortError等を無視（iOS Safariでユーザー操作なしの再生が拒否される場合がある）
    });
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  // Intersection Observer で可視性を検知
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // 可視状態になったら動画URLを取得
  useEffect(() => {
    if (!isVisible || !claim.fileName) return;

    const fetchVideo = async () => {
      try {
        const url = await getNftMediaUrl({
          fileName: claim.fileName!,
          raceAlias: claim.raceAlias,
        });
        setVideoUrl(url);
      } catch (err) {
        console.error('Failed to load video:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [isVisible, claim.fileName, claim.raceAlias]);

  return (
    <Box
      ref={(node: HTMLDivElement | null) => {
        (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
    >
      <Card
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          position: 'relative',
          background: theme.palette.background.paper,
          border: 'none',
          borderRadius: 2,
          transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            padding: '2px',
            background: gradient,
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
          ...(highlighted && {
            animation: `${pulseGlow} 1.5s ease-in-out infinite`,
          }),
        }}
      >
        {!isVisible || loading ? (
          <Skeleton variant="rectangular" sx={{ aspectRatio: '1', m: 0.5, borderRadius: 1 }} />
        ) : error || !videoUrl ? (
          <Box
            sx={{
              aspectRatio: '1',
              m: 0.5,
              borderRadius: 1,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary" variant="caption">
              読み込み失敗
            </Typography>
          </Box>
        ) : (
          <Box sx={{ aspectRatio: '1', m: 0.5, borderRadius: 1, overflow: 'hidden' }}>
            <video
              ref={videoRef}
              src={videoUrl}
              muted
              playsInline
              preload="metadata"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}
      </Card>

      {/* ゼッケン番号（カード外） */}
      <Typography
        sx={{
          display: 'block',
          textAlign: 'center',
          pt: 0.75,
          pb: 0.5,
          fontWeight: 700,
          fontSize: '0.8rem',
          background: gradient,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '0.08em',
        }}
      >
        No.{claim.bib}
      </Typography>
    </Box>
  );
});
